import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Clock, Copy, Info, Sparkles, Tag, X } from 'lucide-react';
import { formatOfferTimeLeft } from '../utils/offerCountdown';

const COUNTDOWN_REFRESH_MS = 30 * 1000;
const COPIED_MESSAGE_MS = 2000;

// Every offer says this: the discount comes off the first payment only, on
// every plan (1 month and 3 months renew; Permanent Access is paid once), and
// renewals go back to the regular price. It is not left to whoever writes the
// offer, so it can't be forgotten.
const OFFER_FINE_PRINT = 'The discount applies to your first payment only. After that, subscriptions renew at the regular price.';

// A limited-time discount announcement: the offer, its promo code with a
// one-click copy, how to use the code at checkout and a way to subscribe. The
// code is a Stripe promotion code, so it is entered on the checkout page
// ("Add promotion code"), not in the Redeem code dialog (those are access
// codes). Only accounts without access are sent one (see the backend's
// _announcement_audience). `preview` is the administrator's view of it while
// writing one: nothing in it can be clicked through.
const OfferBanner = ({ announcement, onDismiss, onAction, dismissing = false, count = 1, preview = false }) => {
  const [copied, setCopied] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const copiedTimerRef = useRef(null);

  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), COUNTDOWN_REFRESH_MS);
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(copiedTimerRef.current);
    };
  }, []);

  const code = announcement.promo_code || '';
  const timeLeft = formatOfferTimeLeft(announcement.expires_at, now);
  const actionLabel = announcement.action_label || 'Subscribe now';
  const actionPath = announcement.action_path || '/subscription';

  const copyCode = async () => {
    if (!code || preview) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = window.setTimeout(() => setCopied(false), COPIED_MESSAGE_MS);
    } catch (error) {
      // No clipboard access: the code is selectable text, so it can still be copied by hand.
    }
  };

  const action = preview ? (
    <span className="site-button site-button--primary !min-h-10 !px-5 shrink-0" data-testid="offer-action">
      {actionLabel} <ArrowRight size={14} aria-hidden="true" />
    </span>
  ) : (
    <Link to={actionPath} onClick={onAction} className="site-button site-button--primary !min-h-10 !px-5 shrink-0" data-testid="offer-action">
      {actionLabel} <ArrowRight size={14} aria-hidden="true" />
    </Link>
  );

  return (
    <section
      className="announcement-banner offer-banner"
      aria-label="Limited-time offer"
      aria-live="polite"
      data-testid="offer-banner"
    >
      <div className="mx-auto max-w-[1320px] px-4 py-3.5 pr-12 text-white sm:px-6 sm:pr-14">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-6">
          <div className="offer-banner__icon hidden shrink-0 lg:flex" aria-hidden="true">
            <Tag size={22} />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <span className="offer-banner__badge">
                <Sparkles size={11} aria-hidden="true" />
                Limited-time offer
              </span>
              {timeLeft && (
                <span className="offer-banner__timer" data-testid="offer-time-left">
                  <Clock size={11} aria-hidden="true" />
                  {timeLeft}
                </span>
              )}
            </div>
            <h2 className="mt-1.5 break-words text-base font-bold leading-snug sm:text-lg" data-testid="offer-title">
              {announcement.title}
            </h2>
            <p className="mt-1 whitespace-pre-wrap break-words text-xs leading-relaxed text-[#c8d8ea]">
              {announcement.message}
            </p>
            {code && (
              <ol className="offer-banner__steps mt-2.5 flex flex-col gap-1 text-[11px] text-[#a9bfd6] sm:flex-row sm:flex-wrap sm:gap-x-5" data-testid="offer-steps">
                <li><span>1</span>Choose a plan</li>
                <li><span>2</span>At checkout, open &ldquo;Add promotion code&rdquo;</li>
                <li><span>3</span>Enter <strong className="font-mono tracking-wider text-[#ffe3a3]">{code}</strong> and apply it before paying</li>
              </ol>
            )}
            <p className="mt-2 flex items-start gap-1.5 text-[11px] leading-snug text-[#c8d8ea]" data-testid="offer-fine-print">
              <Info size={12} className="mt-px shrink-0 text-[#f4c86d]" aria-hidden="true" />
              {OFFER_FINE_PRINT}
            </p>
          </div>

          <div className="flex shrink-0 flex-col items-stretch gap-2.5 sm:flex-row sm:items-center">
            {code && (
              <div className="offer-banner__code">
                <div className="flex flex-col leading-none">
                  <span className="offer-banner__code-label">Promo code</span>
                  <span className="offer-banner__code-value select-all" data-testid="offer-code">{code}</span>
                </div>
                <button
                  type="button"
                  onClick={() => void copyCode()}
                  className="offer-banner__copy"
                  aria-label={copied ? `Copied promo code ${code}` : `Copy promo code ${code}`}
                  data-testid="offer-copy"
                  disabled={preview}
                >
                  {copied ? <Check size={13} aria-hidden="true" /> : <Copy size={13} aria-hidden="true" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            )}
            {action}
          </div>
        </div>
      </div>

      {!preview && (
        <div className="absolute right-2 top-2 flex items-center gap-2 sm:right-4 sm:top-3">
          {count > 1 && (
            <span className="font-mono text-[10px] text-[#C0CCEE]">1 / {count}</span>
          )}
          <button
            type="button"
            onClick={onDismiss}
            disabled={dismissing}
            title="Dismiss offer"
            aria-label="Dismiss announcement"
            className="site-icon-button h-8 w-8 shrink-0 disabled:opacity-50"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
      )}
    </section>
  );
};

export default OfferBanner;
